<template>
  <div class="flex flex-col relative bg-white">
    <!-- Header Section -->

    <div class="bg-linear-to-b from-slate-50 to-white">
      <div class="mobile-status-padding"></div>
      <div class="relative">
        <!-- Back Button -->
        <button
          class="mobile-margin-top absolute top-4 left-4 w-11 h-11 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 transition-all duration-300 hover:scale-105 active:scale-95 z-10"
          @click="$emit('close')"
        >
          <svg
            class="w-5 h-5 text-slate-700"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            stroke-width="2.5"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        <!-- Title Section -->
        <div class="px-6 pt-6 pb-8">
          <h1
            class="text-2xl font-bold text-slate-800 text-center tracking-tight"
          >
            Mon panier
          </h1>
          <p class="text-sm text-slate-500 text-center mt-1.5">
            {{ cartItems?.length || 0 }} article{{
              cartItems?.length > 1 ? 's' : ''
            }}
          </p>
        </div>

        <!-- Clear Cart Button -->
        <button
          class="mobile-margin-top absolute top-4 right-4 w-11 h-11 flex items-center justify-center rounded-xl bg-red-50 hover:bg-red-100 transition-all duration-300 hover:scale-105 active:scale-95 z-10"
          @click="tryEmptyCart"
        >
          <svg
            class="w-5 h-5 text-red-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            stroke-width="2"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>
    </div>

    <!-- Divider -->
    <div class="py-3 border-t border-slate-100"></div>

    <!-- Articles Section -->
    <div class="px-5 pb-4">
      <h2 class="text-lg font-bold text-slate-800">Mes articles</h2>
    </div>

    <!-- Cart Items with Swipe Actions -->
    <div class="flex-1 px-4 pb-4 space-y-3">
      <div v-for="(dish, index) in cartItems" :key="`${index}-${dish.id}`">
        <SwipeActions
          :disabled="isItemExpanded(dish)"
          class="w-full"
          :demoSwipe="index == 0 ? 'left' : null"
          @swipe="handleSwipeAction($event, dish)"
        >
          <!-- Right swipe action - Voir (orange) -->
          <template #right>
            <div
              class="rounded-2xl bg-gradient-to-br from-orange-500 to-primary-500 text-white h-full w-full flex flex-col items-center justify-center pl-[50%] rounded-l-lg"
            >
              <svg
                class="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              <span class="text-sm font-semibold mt-1">Voir</span>
            </div>
          </template>

          <!-- Left swipe action - Retirer (red) -->
          <template #left>
            <div
              class="rounded-2xl bg-gradient-to-br from-red-500 to-red-600 text-white h-full w-full flex flex-col items-center justify-center pr-[50%] rounded-r-lg"
            >
              <svg
                class="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              <span class="text-sm font-semibold mt-1">Retirer</span>
            </div>
          </template>

          <!-- Default Content - Cart Item Card -->
          <template #default>
            <div class="w-full">
              <div
                class="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden"
              >
                <!-- Item Image and Info -->
                <div class="p-4 flex items-center gap-4">
                  <div
                    class="w-20 h-20 rounded-xl bg-slate-100 overflow-hidden flex-shrink-0 relative"
                  >
                    <div
                      class="absolute w-full h-full shimmerElement rounded-lg"
                    ></div>
                    <img
                      class="absolute w-full h-full object-cover scale-[1.1]"
                      :src="dish.principalImageUrl || dish.dishImage"
                      alt=""
                    />
                  </div>
                  <div class="flex-1 min-w-0">
                    <h3 class="font-bold text-slate-800 text-base truncate">
                      {{ dish.title || dish.dishName }}
                    </h3>
                    <p class="text-sm text-slate-500 truncate">
                      {{ dish.storeName }}
                    </p>
                    <div class="flex items-center justify-between mt-2">
                      <div class="flex items-baseline gap-2">
                        <span
                          v-if="dish.isDeal"
                          class="text-sm text-slate-400 line-through"
                        >
                          {{ Commons.formatNumber(dish.discountedPrice) }}
                        </span>
                        <span class="text-lg font-bold text-slate-800">
                          {{ Commons.formatNumber(dish.price) }} FCFA
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Quantity Controls and Total -->
                <div class="flex items-center justify-between px-4 pb-4">
                  <div
                    class="flex items-center gap-2 bg-slate-50 rounded-lg p-1"
                  >
                    <!-- Decrease Button -->
                    <button
                      class="w-8 h-8 rounded-lg bg-white shadow-sm flex items-center justify-center text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      @click="decreaseItem(dish)"
                      :disabled="dish.quantity <= 1"
                    >
                      <svg
                        class="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2.5"
                          d="M20 12H4"
                        />
                      </svg>
                    </button>

                    <!-- Quantity Display -->
                    <span class="w-8 text-center font-semibold text-slate-800">
                      {{ dish.quantity }}
                    </span>

                    <!-- Increase Button -->
                    <button
                      class="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-primary-500 text-white shadow-md flex items-center justify-center hover:from-orange-600 hover:to-primary-600 transition-all"
                      @click="increaseItem(dish)"
                    >
                      <svg
                        class="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2.5"
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                    </button>
                  </div>

                  <!-- Total Price -->
                  <div class="text-right">
                    <span class="text-sm text-slate-500">Total</span>
                    <div class="font-bold text-slate-800">
                      {{ Commons.formatNumber(dish.totalPrice) }} FCFA
                    </div>
                  </div>
                </div>
                <!-- Expandable Options & Drinks Editor -->
                <div
                  v-if="hasOptionsOrDrinks(dish)"
                  class="border-t border-slate-100"
                >
                  <button
                    @click="toggleItemExpanded(dish)"
                    class="w-full px-4 py-3 flex items-center justify-between text-sm text-slate- transition-colors"
                    :class="{ 'bg-slate-50': isItemExpanded(dish) }"
                  >
                    <span class="flex items-center gap-2">
                      Options & plus
                    </span>
                    <svg
                      class="w-4 h-4 transition-transform duration-200"
                      :class="{ 'rotate-180': isItemExpanded(dish) }"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  <!-- Expanded Content - Use v-show instead of v-if to prevent re-initialization -->
                  <div
                    v-show="isItemExpanded(dish)"
                    class="rounded-t-2xl overflow-hidden -mt-2"
                  >
                    <!-- {{dish}} -->
                    <div v-if="dish">
                      <DishOptionsChoices
                        :dishData="{
                          options: dish.fullOptions,
                          accompaniments: dish.fullAccompaniments
                        }"
                        :selectedOptions="dish.options"
                        @update:options="handleOptionsUpdate"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </SwipeActions>
      </div>

      <div v-if="!cartItems?.length" class="text-center py-8">
        <div
          class="w-32 h-32 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center"
        >
          <svg
            class="w-16 h-16 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
            ></path>
          </svg>
        </div>
        <p class="text-gray-500">Votre panier est vide</p>
      </div>
    </div>

    <div class="py-3"></div>
    <div
      class="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent"
    ></div>
    <div class="py-4"></div>

    <!-- Global Drinks Section -->
    <div
      v-if="
        cartItems?.length &&
        (props.cartData?.restaurantId || cartItems[0]?.storeId)
      "
      class="px-5 pb-6"
    >
      <!-- <h2 class="text-lg font-bold text-slate-800 mb-4">
        Boissons pour toute la commande
      </h2> -->
      <div class="">
        <DrinkChoices
          :restaurantId="props.cartData?.restaurantId || cartItems[0]?.storeId"
          :selectedDrinks="props.cartData?.drinks || []"
          @update:drinks="handleGlobalDrinksUpdate"
        />
      </div>
    </div>

    <!-- Code Promo Section -->
    <div class="py-3"></div>
    <div
      class="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent"
    ></div>
    <div class="py-4"></div>
    <div class="px-5">
      <h2 class="text-lg font-bold text-slate-800 mb-4">Code Promo</h2>

      <!-- Applied Coupon Display -->
      <div
        v-if="props.cartData?.coupon"
        class="flex items-center gap-4 p-4 bg-emerald-50 rounded-xl border border-emerald-100 transition-all duration-300"
      >
        <div
          class="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center shadow-sm"
        >
          <svg
            class="w-5 h-5 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <div class="flex-1">
          <div
            class="text-xs text-emerald-600 font-bold uppercase tracking-wider"
          >
            Coupon appliqué
          </div>
          <div class="font-bold text-slate-800">
            {{ props.cartData.coupon.code }}
          </div>
          <div class="text-xs text-slate-500">
            {{ props.cartData.coupon.description || 'Réduction appliquée' }}
          </div>
        </div>
        <button
          @click="removeCoupon"
          class="p-2 text-slate-400 hover:text-red-500 transition-colors"
        >
          <svg
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>

      <!-- Add Coupon Button -->
      <div
        v-else
        class="flex items-center gap-4 p-4 bg-gradient-to-r from-orange-50 to-primary-50 rounded-xl border border-orange-100 cursor-pointer hover:shadow-md transition-all duration-300 hover:scale-[1.01]"
        @click="promptPromoCode"
      >
        <div
          class="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-primary-500 flex items-center justify-center shadow-sm"
        >
          <svg
            class="w-5 h-5 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
            />
          </svg>
        </div>
        <div class="flex-1">
          <span class="font-bold text-slate-800">Ajouter un code promo</span>
        </div>
        <svg
          class="w-5 h-5 text-slate-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 5l7 7-7 7"
          />
        </svg>
      </div>
    </div>

    <!-- Note au restaurant -->
    <div class="py-4"></div>
    <div
      class="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent"
    ></div>
    <div class="py-4"></div>
    <div class="px-5">
      <h2 class="text-lg font-bold text-slate-800 mb-4">
        Instructions pour le restaurant
      </h2>
      <div class="relative group">
        <textarea
          v-model="customerNote"
          placeholder="Ex: Pas d'oignons, livraison à l'étage, précisions sur l'itinéraire..."
          maxlength="300"
          class="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl text-slate-700 placeholder:text-slate-400 focus:bg-white focus:border-orange-500/50 focus:ring-4 focus:ring-orange-500/10 transition-all duration-300 resize-none h-28 text-sm leading-relaxed"
        ></textarea>
        <div
          class="absolute right-4 bottom-4 text-[10px] font-bold text-slate-400 uppercase tracking-wider group-focus-within:text-orange-500 transition-colors"
        >
          {{ customerNote?.length || 0 }}/300
        </div>
      </div>
    </div>

    <!-- Delivery Section -->
    <div class="py-4"></div>
    <div
      class="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent"
    ></div>
    <div class="py-4"></div>
    <div class="px-5">
      <h2 class="text-lg font-bold text-slate-800 mb-4">Livraison</h2>

      <!-- Selected Delivery Option Display -->
      <div
        @click="openDeliveryOptionsDialog"
        class="flex flex-col gap-2.5 p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer"
        :class="
          isPreloadedLocation
            ? 'border-amber-200 bg-amber-50/40 hover:border-amber-300 hover:bg-amber-50/60 shadow-xs'
            : 'border-slate-100 bg-white hover:border-slate-200 shadow-xs'
        "
      >
        <div class="flex items-center gap-4">
          <div
            class="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
            :class="
              isPreloadedLocation
                ? 'bg-amber-100'
                : getDeliveryOptionIconBg()
            "
          >
            <svg
              class="w-6 h-6"
              :class="
                isPreloadedLocation
                  ? 'text-amber-600'
                  : getDeliveryOptionIconColor()
              "
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              v-html="getDeliveryOptionIcon()"
            ></svg>
          </div>
          <div class="flex-1 min-w-0">
            <span class="font-semibold text-slate-800 block truncate">
              {{ getDeliveryOptionLabel() }}
            </span>
            <span class="text-sm text-slate-600 font-medium block truncate mt-0.5">
              {{ getDeliveryOptionDescription() }}
            </span>
            <span
              v-if="
                selectedDeliveryLocation &&
                (selectedDeliveryLocation.contactName ||
                  selectedDeliveryLocation.description)
              "
              class="text-xs text-slate-400 block truncate mt-0.5"
            >
              {{
                selectedDeliveryLocation.contactName
                  ? `${selectedDeliveryLocation.contactName}${
                      selectedDeliveryLocation.contactPhone
                        ? ' · ' + selectedDeliveryLocation.contactPhone
                        : ''
                    }`
                  : selectedDeliveryLocation.description
              }}
            </span>
          </div>
          <button
            class="px-3.5 py-2 rounded-lg text-sm font-semibold flex-shrink-0 transition-all"
            :class="
              isPreloadedLocation
                ? 'bg-amber-500 text-white hover:bg-amber-600 shadow-sm'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            "
          >
            {{
              isPreloadedLocation
                ? 'Vérifier'
                : selectedDeliveryLocation?.address
                ? 'Modifier'
                : 'Sélectionner'
            }}
          </button>
        </div>

        <!-- Warning banner if preloaded/stored from previous selection -->
        <div
          v-if="isPreloadedLocation"
          class="flex items-center gap-2 text-xs text-amber-800 font-medium pt-2 border-t border-amber-200/60"
        >
          <svg
            class="w-4 h-4 text-amber-600 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span class="leading-tight">
            Mémorisée lors d'une sélection précédente. Toujours là ?
          </span>
        </div>
      </div>

      <!-- Estimated time banner (shown after fees are calculated) -->
      <div
        v-if="calculatedFees.durationMin > 0"
        class="mt-3 flex items-center gap-3 px-4 py-3 bg-orange-50 border border-orange-100 rounded-xl"
      >
        <svg
          class="w-5 h-5 text-orange-500 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <div class="flex-1">
          <span
            class="text-xs text-orange-600 font-bold uppercase tracking-wide"
            >Temps estimé</span
          >
          <div class="text-sm font-bold text-orange-700">
            Livraison en
            {{ (calculatedFees.durationMin || 0) + estimatedCookingTime }} min
            <span class="text-orange-400 font-normal text-xs ml-1"
              >({{ estimatedCookingTime }} min prépa +
              {{ calculatedFees.durationMin }} min livraison)</span
            >
          </div>
        </div>
      </div>
    </div>

    <!-- Totals Section -->
    <div class="py-4"></div>
    <div
      class="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent"
    ></div>
    <div class="py-4"></div>
    <div class="flex flex-col px-5 gap-3">
      <div class="flex justify-between items-center">
        <span class="text-slate-600">Sous total</span>
        <span class="font-semibold text-slate-800">
          {{ Commons.formatNumber(subtotal) }} FCFA
        </span>
      </div>
      <div class="flex justify-between items-center">
        <span class="text-slate-600">Total boissons</span>
        <span class="font-semibold text-slate-800">
          {{ Commons.formatNumber(drinksTotal) }} FCFA
        </span>
      </div>
      <div
        v-if="calculatedFees.deliveryFee !== undefined && deliveryOption"
        class="flex justify-between items-center mt-1"
      >
        <span class="text-slate-600">Frais de livraison</span>
        <div class="flex flex-col items-end text-right">
          <span
            v-if="
              calculatedFees.deliveryPromotion?.discountAmount > 0 &&
              calculatedFees.originalDeliveryFee > calculatedFees.deliveryFee
            "
            class="text-xs text-slate-400 line-through"
          >
            {{ Commons.formatNumber(calculatedFees.originalDeliveryFee) }}
          </span>
          <span
            :class="{
              'text-emerald-600 font-bold':
                calculatedFees.deliveryPromotion?.discountAmount > 0,
              'font-semibold text-slate-800': !(
                calculatedFees.deliveryPromotion?.discountAmount > 0
              )
            }"
          >
            {{
              calculatedFees.deliveryFee === 0 &&
              deliveryOption === 'manualyTook'
                ? 'Gratuit'
                : Commons.formatNumber(calculatedFees.deliveryFee) + ' FCFA'
            }}
            <span
              v-if="calculatedFees.deliveryPromotion?.discountAmount > 0"
              class="text-[10px] bg-emerald-100 text-emerald-600 px-1 rounded ml-1"
            >
              PROMO
            </span>
          </span>
        </div>
      </div>
      <!-- Service Fee -->
      <div
        v-if="serviceFee && serviceFee > 0"
        class="flex justify-between items-center mt-1"
      >
        <span class="text-slate-600">Frais de service</span>
        <span class="font-semibold text-slate-800">
          {{ Commons.formatNumber(serviceFee) }} FCFA
        </span>
      </div>
      <!-- Distance -->
      <div
        v-if="calculatedFees.distanceFormatted"
        class="flex justify-between items-center mt-1"
      >
        <span class="text-slate-600">Distance</span>
        <span class="font-semibold text-slate-800">
          {{ calculatedFees.distanceFormatted }}
        </span>
      </div>
      <div v-if="totalDiscount > 0" class="flex justify-between items-center">
        <span class="text-emerald-600 font-medium">Réduction</span>
        <span class="font-bold text-emerald-600">
          -{{ Commons.formatNumber(totalDiscount) }} FCFA
        </span>
      </div>
    </div>

    <!-- Fixed Bottom Section -->
    <div class="py-4"></div>
    <div class="mobile-padding"></div>

    <div class="sticky bottom-0">
      <div
        class="h-[60px] z-[2] relative"
        style="background: linear-gradient(to top, #fff 10%, #0000)"
      ></div>
      <div class="h-[100px] bg-white z-[2] relative"></div>
      <div
        class="h-[100px] shadow-xl shadow-slate-200/50 absolute bottom-0 rounded-t-2xl w-full"
      ></div>
      <div
        class="w-full absolute bottom-0 rounded-t-2xl px-5 pt-5 pb-6 bg-white z-[4] border-t border-slate-100"
      >
        <div class="flex items-center justify-between mb-3">
          <span class="text-slate-600">Total à payer</span>
          <span class="text-2xl font-bold text-slate-800">
            {{ Commons.formatNumber(grandTotal) }} FCFA
          </span>
        </div>
        <button
          class="w-full buttonComponent text-lg! font-bold! hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
          @click="handleConfirmOrder"
          :disabled="!cartItems?.length"
        >
          Confirmer la commande
        </button>
        <div class="mobile-padding"></div>
        <div class="py-2"></div>
      </div>
    </div>

    <!-- Payment Dialog -->
    <CartPaymentDialog
      ref="paymentDialog"
      :subtotal="calculatedFees.subtotal"
      :total="grandTotal"
      :deliveryFee="calculatedFees.deliveryFee"
      :serviceFee="calculatedFees.serviceFee"
      :paymentFee="calculatedFees.paymentFee"
      :discount="totalDiscount||0"
      @paymentConfirmed="onPaymentConfirmed"
      @cancelled="onPaymentCancelled"
    />

    <!-- Delivery Options Dialog -->
    <DeliveryOptionsDialog
      ref="deliveryOptionsDialog"
      :currentOption="deliveryOption"
      @optionSelected="onDeliveryOptionSelected"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';
import Commons from '@features/shared/libraries/commons';
import SwipeActions from '@ui/SwipeActions.global.vue';
import CartPaymentDialog from '@features/cart/components/CartPaymentDialog.bottomsheet.vue';
import DeliveryOptionsDialog from '@features/cart/components/DeliveryOptionsDialog.bottomsheet.vue';

// Props
const props = defineProps({
  cartData: {
    type: Object,
    default: () => ({})
  },
  // Optional: map of dishId -> full dishData for editing options/drinks
  dishesDataMap: {
    type: Object,
    default: () => ({})
  }
});

// Emits
const emit = defineEmits([
  'close',
  'updateItem',
  'removeItem',
  'viewItem',
  'addPromoCode',
  'selectDeliveryOption',
  'confirmOrder',
  'updateOptions', // Match CartDialog handler
  'updateDrinks' // Match CartDialog handler
]);

// State
const STORAGE_KEY_LOCATION = 'rapidoo_last_delivery_location';
const STORAGE_KEY_DELIVERY_OPTION = 'rapidoo_last_delivery_option';

const getStoredDeliveryLocation = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY_LOCATION);
    return data ? JSON.parse(data) : null;
  } catch (e) {
    console.warn('Failed to load delivery location from localStorage', e);
    return null;
  }
};

const getStoredDeliveryOption = () => {
  try {
    return localStorage.getItem(STORAGE_KEY_DELIVERY_OPTION) || '';
  } catch (e) {
    return '';
  }
};

const saveStoredDeliveryLocation = (location, option) => {
  try {
    if (location) {
      localStorage.setItem(STORAGE_KEY_LOCATION, JSON.stringify(location));
    }
    if (option) {
      localStorage.setItem(STORAGE_KEY_DELIVERY_OPTION, option);
    }
  } catch (e) {
    console.warn('Failed to save delivery location to localStorage', e);
  }
};

const savedLocation = getStoredDeliveryLocation();
const savedOption = getStoredDeliveryOption();

const expandedItems = ref({});
const deliveryOption = ref(savedOption || (savedLocation ? 'actualPosition' : ''));
const selectedDeliveryLocation = ref(savedLocation || null);
const locationReconfirmed = ref(false);
const paymentDialog = ref(null);
const deliveryOptionsDialog = ref(null);
const calculatedFees = ref({
  subtotal: 0,
  deliveryFee: 0,
  serviceFee: 0,
  paymentFee: 0,
  total: 0
});
const isCalculatingFees = ref(false);
const customerNote = ref('');

// Additional fields for location picker
const locationAdditionalFields = [
  {
    key: 'contactName',
    label: 'Nom du contact',
    type: 'text',
    placeholder: 'Votre nom',
    required: true
  },
  {
    key: 'contactPhone',
    label: 'Téléphone à contacter',
    type: 'tel',
    placeholder: '+229 XX XX XX XX',
    required: true
  }
];

// Computed
const cartItems = computed(() => props.cartData?.items || []);

const isPreloadedLocation = computed(() => {
  return (
    !!selectedDeliveryLocation.value &&
    !locationReconfirmed.value &&
    deliveryOption.value !== 'manualyTook'
  );
});

const subtotal = computed(() => {
  return (
    calculatedFees.value.itemsSubtotal ||
    props.cartData?.totalItemsCost ||
    props.cartData?.subtotal ||
    props.cartData?.totalAmount ||
    0
  );
});

const drinksTotal = computed(() => {
  return (
    calculatedFees.value.drinksSubtotal || props.cartData?.drinksTotal || 0
  );
});

const totalDiscount = computed(() => {
  return (
    calculatedFees.value.discount ||
    props.cartData?.totalDiscount ||
    props.cartData?.discount ||
    0
  );
});

const serviceFee = computed(() => {
  return calculatedFees.value.serviceFee || props.cartData?.serviceFee || 0;
});

const grandTotal = computed(() => {
  // if (calculatedFees.value.total) {
  //   return calculatedFees.value.total;
  // }
  return  props.cartData?.total || 0;
});

const estimatedCookingTime = computed(() => {
  if (!cartItems.value || cartItems.value.length === 0) return 0;
  const maxTime = Math.max(
    ...cartItems.value.map(
      (item) =>
        parseInt(item.cookDurationInMin) || parseInt(item.preparationTime) || 15
    )
  );
  return cartItems.value.length > 1 ? maxTime + 5 : maxTime;
});

const isConfirmingOrder = ref(false);

const calculateFeesForCurrentLocation = () => {
  if (
    deliveryOption.value !== 'manualyTook' &&
    !selectedDeliveryLocation.value
  ) {
    return;
  }

  const restaurantId =
    props.cartData?.restaurantId || cartItems.value[0]?.storeId;
  if (!restaurantId) return;

  isCalculatingFees.value = true;

  const feesPayload = {
    restaurantId,
    deliveryOption: deliveryOption.value
  };

  if (deliveryOption.value !== 'manualyTook') {
    feesPayload.deliveryAddress = {
      lat: selectedDeliveryLocation.value?.lat,
      lng: selectedDeliveryLocation.value?.lng,
      address:
        selectedDeliveryLocation.value?.address ||
        selectedDeliveryLocation.value?.description,
      city: selectedDeliveryLocation.value?.city || 'Cotonou'
    };
  } else {
    feesPayload.isPickup = true;
  }
  console.log(
    '🚀 ~ calculateFeesForCurrentLocation ~ feesPayload:',
    feesPayload
  );

  bus.emit(constants.EVENTS.CART.ORDER_FEES_CALCULATE, feesPayload);
};

// Methods
const isItemExpanded = (dish) => {
  return expandedItems.value[dish.id] || false;
};

const toggleItemExpanded = (dish) => {
  expandedItems.value[dish.id] = !expandedItems.value[dish.id];
};

const hasOptionsOrDrinks = (dish) => {
  return (
    (dish.fullAccompaniments && dish.fullAccompaniments.length > 0) ||
    (dish.fullOptions && dish.fullOptions.length > 0) ||
    (dish.drinks && dish.drinks.length > 0)
  );
};

const handleSwipeAction = ({ sideVisible, reset }, dish) => {
  reset();

  if (sideVisible === 'right') {
    emit('close');
    bus.emit('dish.SHOW_DISH_DETAIL', {
      id: dish.dishId
    });
  } else if (sideVisible === 'left') {
    // Swipe left - Remove item with confirmation
    if (props.cartData?.items?.length === 1) {
      tryEmptyCart();
    } else confirmRemoveItem(dish);
  }
};

const confirmRemoveItem = async (dish) => {
  try {
    const result = await window.customPrompt?.({
      title: 'Retirer du panier',
      message: `Voulez-vous retirer ${dish.title || dish.dishName} de votre panier ?`,
      successText: 'Retirer du panier',
      cancelText: 'Retour'
    });

    if (result?.accepted) {
      emit('removeItem', dish.id || dish._id);
    }
  } catch (error) {
    console.error('Error showing prompt:', error);
  }
};

const increaseItem = (dish) => {
  emit('updateItem', {
    itemId: dish.id || dish._id,
    quantity: dish.quantity + 1
  });
};

const decreaseItem = (dish) => {
  if (dish.quantity > 1) {
    emit('updateItem', {
      itemId: dish.id || dish._id,
      quantity: dish.quantity - 1
    });
  } else {
    // If quantity would go to 0, remove item instead
    emit('removeItem', dish.id || dish._id);
  }
};

const tryEmptyCart = async () => {
  try {
    const result = await window.customPrompt?.({
      title: 'Vider le panier',
      message: 'Voulez-vous vraiment vider votre panier ?',
      successText: 'Vider le panier',
      cancelText: 'Retour'
    });

    if (result?.accepted) {
      // Emit CART.CLEAR event to clear the cart
      bus.emit(constants.EVENTS.CART.CLEAR);
    }
  } catch (error) {
    console.error('Error showing prompt:', error);
  }
};

const selectDeliveryOption = (option) => {
  deliveryOption.value = option;
  emit('selectDeliveryOption', option);
};

// Open delivery options dialog
const openDeliveryOptionsDialog = () => {
  deliveryOptionsDialog.value?.showModal({
    requestKey: 'cart_delivery_location',
    title: 'Mode de livraison',
    currentOption: deliveryOption.value || 'actualPosition',
    currentLocation: selectedDeliveryLocation.value,
    defaultPosition: selectedDeliveryLocation.value
      ? {
          lat:
            selectedDeliveryLocation.value.lat ??
            selectedDeliveryLocation.value.coordinates?.lat ??
            0,
          lng:
            selectedDeliveryLocation.value.lng ??
            selectedDeliveryLocation.value.coordinates?.lng ??
            0
        }
      : null,
    defaultValues: selectedDeliveryLocation.value
      ? {
          contactName: selectedDeliveryLocation.value.contactName,
          contactPhone: selectedDeliveryLocation.value.contactPhone,
          description: selectedDeliveryLocation.value.description
        }
      : null
  });
};

const removeCoupon = () => {
  bus.emit(constants.EVENTS.CART.COUPON.REMOVE);
};

const promptPromoCode = async () => {
  try {
    const result = await window.customPrompt?.({
      title: 'Code Promo',
      message: 'Veuillez saisir votre code promotionnel ci-dessous :',
      successText: 'Appliquer',
      cancelText: 'Annuler',
      inputs: [
        {
          key: 'code',
          type: 'text',
          label: 'Code Coupon',
          placeholder: 'PROMO2024',
          required: true,
          helper: "Entrez le code tel qu'il vous a été communiqué"
        }
      ]
    });

    if (result?.accepted && result.data?.code) {
      bus.emit(constants.EVENTS.CART.COUPON.APPLY, { code: result.data.code });
    }
  } catch (error) {
    console.error('Error showing promo prompt:', error);
  }
};

// Handle delivery option selection from dialog
const onDeliveryOptionSelected = (option) => {
  deliveryOption.value = option;
  emit('selectDeliveryOption', option);

  // For "manualyTook" (pickup), clear the delivery location
  // This ensures the API knows it's not a delivery order
  if (option === 'manualyTook') {
    selectedDeliveryLocation.value = null;
    calculateFeesForCurrentLocation();
  } else if (selectedDeliveryLocation.value) {
    saveStoredDeliveryLocation(selectedDeliveryLocation.value, option);
  }
};

// Helper methods for delivery option display
const getDeliveryOptionLabel = () => {
  switch (deliveryOption.value) {
    case 'manualyTook':
      return 'Retrait au restaurant';
    case 'other':
      return "Carnet d'adresses";
    case 'actualPosition':
    default:
      return 'Adresse de livraison';
  }
};

const getDeliveryOptionDescription = () => {
  switch (deliveryOption.value) {
    case 'actualPosition':
    case 'other':
      return selectedDeliveryLocation.value
        ? selectedDeliveryLocation.value.address || 'Position sélectionnée'
        : 'Sélectionner une adresse de livraison';
    case 'manualyTook':
      return 'Retrait au restaurant';
    default:
      return selectedDeliveryLocation.value?.address || 'Où être livré ?';
  }
};

const getDeliveryOptionIconBg = () => {
  return 'bg-slate-100';
};

const getDeliveryOptionIconColor = () => {
  return 'text-slate-600';
};

const getDeliveryOptionIcon = () => {
  switch (deliveryOption.value) {
    case 'actualPosition':
      return `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />`;
    case 'other':
      return `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />`;
    case 'manualyTook':
      return `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />`;
    default:
      return `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />`;
  }
};

// Check if we can edit options for this item (have dishData with options)
const canEditOptions = (dish) => {
  const dishData =
    props.dishesDataMap[dish.dishId] || props.dishesDataMap[dish.id];
  return dishData?.options && dishData.options.length > 0;
};

// Check if we can edit drinks for this item (have restaurantId and dishData)
const canEditDrinks = (dish) => {
  return dish.storeId && props.dishesDataMap[dish.dishId || dish.id];
};

// Get full dishData for an item
const getDishDataForItem = (dish) => {
  return props.dishesDataMap[dish.dishId] || props.dishesDataMap[dish.id] || {};
};

// Get the currently expanded dish
const getExpandedDish = () => {
  const expandedId = Object.keys(expandedItems.value).find(
    (id) => expandedItems.value[id]
  );
  return cartItems.value.find(
    (dish) => dish.id === expandedId || dish._id === expandedId
  );
};

// Handle options update from DishOptionsChoices component
const handleOptionsUpdate = (newOptions) => {
  const dish = getExpandedDish();
  if (dish) {
    updateItemOptions(dish, newOptions);
  }
};

// Handle drinks update from DrinkChoices component
const handleDrinksUpdate = (newDrinks) => {
  const dish = getExpandedDish();
  if (dish) {
    updateItemDrinks(dish, newDrinks);
  }
};

// Handle global drinks update
const handleGlobalDrinksUpdate = (newDrinks) => {
  bus.emit(constants.EVENTS.CART.DRINKS.UPDATE, {
    drinks: newDrinks
  });
};

// Update item options - emit to parent
const updateItemOptions = (dish, options) => {
  emit('updateOptions', {
    itemId: dish.id || dish._id,
    options: options
  });
};

// Update item drinks - emit to parent
const updateItemDrinks = (dish, drinks) => {
  emit('updateDrinks', {
    itemId: dish.id || dish._id,
    drinks: drinks
  });
};

// Listen for location selection from MapLocationPickerDialog
const handleLocationSelected = ({ requestKey, selectedLocation }) => {
  if (requestKey === 'cart_delivery_location') {
    selectedDeliveryLocation.value = selectedLocation;
    locationReconfirmed.value = true;
    saveStoredDeliveryLocation(
      selectedLocation,
      deliveryOption.value || 'actualPosition'
    );
    calculateFeesForCurrentLocation();
  }
};

// Modified confirm order to check for delivery location first
const handleConfirmOrder = async () => {
  if (
    deliveryOption.value !== 'manualyTook' &&
    (!selectedDeliveryLocation.value || !locationReconfirmed.value)
  ) {
    openDeliveryOptionsDialog();
    return;
  }

  isConfirmingOrder.value = true;
  calculateFeesForCurrentLocation();
};

// Handle successful fees calculation
const handleFeesCalculated = (payload) => {
  isCalculatingFees.value = false;

  // Extraire les frais selon la structure de l'API { fees: {...}, cart: {...} }
  const feesData = payload.fees || payload;

  // Update calculated fees with API response
  calculatedFees.value = {
    subtotal: feesData.subtotal || subtotal.value,
    itemsSubtotal: feesData.itemsSubtotal || 0,
    drinksSubtotal: feesData.drinksSubtotal || 0,
    deliveryFee: feesData.deliveryFee || 0,
    originalDeliveryFee:
      feesData.originalDeliveryFee !== undefined
        ? feesData.originalDeliveryFee
        : feesData.deliveryFee || 0,
    deliveryPromotion: feesData.deliveryPromotion || null,
    serviceFee: feesData.serviceFee || 0,
    paymentFee: feesData.paymentFee || 0,
    discount: feesData.discount || 0,
    total: feesData.total || 0,
    // Preserve distance/duration for order creation payload
    distanceKm: feesData.distanceKm || 0,
    durationMin: feesData.durationMin || 0,
    distanceFormatted: feesData.distanceFormatted || '',
    durationFormatted: feesData.durationFormatted || ''
  };

  // Only open payment dialog if user explicitly confirmed the order
  if (isConfirmingOrder.value) {
    paymentDialog.value?.showModal();
    isConfirmingOrder.value = false;
  }
};

// Handle fees calculation error
const handleFeesCalculateError = (error) => {
  isCalculatingFees.value = false;
  isConfirmingOrder.value = false;
  console.error('[CartBottomSheet] Fees calculation error:', error);

  bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
    type: 'error',
    title: 'Erreur de calcul',
    body:
      error?.message || 'Impossible de calculer les frais. Veuillez réessayer.'
  });
};

// Handle payment confirmation from dialog
const onPaymentConfirmed = (paymentData) => {
  // For "manualyTook" (pickup), provide a default delivery location format
  // that won't be blocked by the API validation
  let deliveryLocationToSend = selectedDeliveryLocation.value;

  if (deliveryOption.value === 'manualyTook') {
    // Send a minimal valid location for pickup orders
    // The API will recognize this as a pickup order and handle it appropriately
    deliveryLocationToSend = {
      lat: 0,
      lng: 0,
      address: 'Pickup at restaurant',
      city: 'Cotonou',
      description: 'Customer will pick up order',
      isPickup: true // Flag to indicate this is not a delivery
    };
  }

  // Calculate estimated cooking time
  let estimatedCookingTimeMin = 0;
  if (cartItems.value && cartItems.value.length > 0) {
    const maxTime = Math.max(
      ...cartItems.value.map(
        (item) =>
          parseInt(item.cookDurationInMin) ||
          parseInt(item.preparationTime) ||
          15
      )
    );
    estimatedCookingTimeMin =
      cartItems.value.length > 1 ? maxTime + 5 : maxTime;
  }

  // Include delivery location, distance/duration and payment data in confirmOrder emit
  emit('confirmOrder', {
    deliveryOption: deliveryOption.value,
    deliveryLocation: deliveryLocationToSend,
    distanceKm: calculatedFees.value.distanceKm || 0,
    durationMin: calculatedFees.value.durationMin || 0,
    estimatedCookingTimeMin,
    paymentData,
    customerNote: customerNote.value
  });
};

const onPaymentCancelled = () => {
  // Payment dialog was cancelled - user stays in cart
  console.log('[CartBottomSheet] Payment cancelled');
};

let locationHandlerId;
let deliveryBookLocationHandlerId;
let feesCalculatedHandlerId;
let feesCalculateErrorHandlerId;
let itemUpdatingHandlerId;
let itemUpdatedHandlerId;
let optionsUpdatingHandlerId;
let optionsUpdatedHandlerId;
let drinksUpdatingHandlerId;
let drinksUpdatedHandlerId;

onMounted(() => {
  locationHandlerId = bus.on(
    constants.EVENTS.MAP.LOCATION_SELECTED,
    handleLocationSelected
  );

  deliveryBookLocationHandlerId = bus.on(
    'delivery_location_confirmed',
    handleLocationSelected
  );

  // Listen for fees calculation events (with Google Maps distance)
  feesCalculatedHandlerId = bus.on(
    constants.EVENTS.CART.ORDER_FEES_CALCULATED,
    handleFeesCalculated
  );

  feesCalculateErrorHandlerId = bus.on(
    constants.EVENTS.CART.ORDER_FEES_CALCULATE_ERROR,
    handleFeesCalculateError
  );

  // If a location is restored or pickup is selected, calculate fees immediately
  if (
    selectedDeliveryLocation.value ||
    deliveryOption.value === 'manualyTook'
  ) {
    calculateFeesForCurrentLocation();
  }

  // Listen for cart item updates to show/hide loading indicators
  // itemUpdatingHandlerId = bus.on(constants.EVENTS.CART.ITEMS.UPDATING, handleItemUpdating);
  // itemUpdatedHandlerId = bus.on(constants.EVENTS.CART.ITEMS.UPDATED, handleItemUpdated);

  // Listen for options/drinks updates
  // optionsUpdatingHandlerId = bus.on(constants.EVENTS.CART.ITEMS.OPTIONS.UPDATING, handleOptionsUpdating);
  // optionsUpdatedHandlerId = bus.on(constants.EVENTS.CART.ITEMS.OPTIONS.UPDATED, handleOptionsUpdated);
  // drinksUpdatingHandlerId = bus.on(constants.EVENTS.CART.ITEMS.DRINKS.UPDATING, handleDrinksUpdating);
  // drinksUpdatedHandlerId = bus.on(constants.EVENTS.CART.ITEMS.DRINKS.UPDATED, handleDrinksUpdated);
});

onUnmounted(() => {
  if (locationHandlerId) {
    bus.off(locationHandlerId);
  }
  if (deliveryBookLocationHandlerId) {
    bus.off(deliveryBookLocationHandlerId);
  }
  if (feesCalculatedHandlerId) {
    bus.off(feesCalculatedHandlerId);
  }
  if (feesCalculateErrorHandlerId) {
    bus.off(feesCalculateErrorHandlerId);
  }
  if (itemUpdatingHandlerId) {
    bus.off(itemUpdatingHandlerId);
  }
  if (itemUpdatedHandlerId) {
    bus.off(itemUpdatedHandlerId);
  }
  if (optionsUpdatingHandlerId) {
    bus.off(optionsUpdatingHandlerId);
  }
  if (optionsUpdatedHandlerId) {
    bus.off(optionsUpdatedHandlerId);
  }
  if (drinksUpdatingHandlerId) {
    bus.off(drinksUpdatingHandlerId);
  }
  if (drinksUpdatedHandlerId) {
    bus.off(drinksUpdatedHandlerId);
  }
});
</script>

<style scoped>
.shimmerElement {
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.05) 0%,
    rgba(0, 0, 0, 0.1) 50%,
    rgba(0, 0, 0, 0.05) 100%
  );
  animation: shimmer 2s infinite;
  background-size: 200% 100%;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.mobile-margin-top {
  @apply mt-[env(safe-area-inset-top)];
}

.mobile-padding {
  @apply pb-[env(safe-area-inset-bottom)];
}
</style>
